import React from "react";
import {
  Box,
  Heading,
  Link,
  List,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

function scrollToBlogSection(e) {
  e.preventDefault();
  const el = document.getElementById("blog-section");
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, '', '#blog-section');
  } else {
    window.location.href = "/blog#blog-section";
  }
}

export default function SafeOnlineBorrowing() {
  return (
    <Box maxW="1000px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 20, md: 32 }}>
      <VStack spacing={6} align="start">
        <Link
          as="button"
          onClick={scrollToBlogSection}
          aria-label="Back to Blog"
          position="fixed"
          top={{ base: "84px", md: "96px" }}
          left={{ base: 4, md: 8 }}
          zIndex="sticky"
          bg="white"
          color="teal.600"
          fontWeight="semibold"
          border="1px solid"
          borderColor="teal.100"
          borderRadius="full"
          px={{ base: 0, sm: 4 }}
          py={{ base: 0, sm: 2 }}
          w={{ base: "42px", sm: "auto" }}
          h={{ base: "42px", sm: "auto" }}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="sm"
          _hover={{ textDecoration: "none", bg: "teal.50" }}
        >
          <Text as="span" display={{ base: "inline", sm: "none" }}>←</Text>
          <Text as="span" display={{ base: "none", sm: "inline" }}>← Back to Blog</Text>
        </Link>

        <Heading as="h1" size="xl" mb={2} mt={16}>Safe Online Borrowing: Security Checklist</Heading>
        <Text fontSize="sm" color="gray.500">
          By Heavenly • March 09, 2026 • 5 mins read
        </Text>

        <Text>
          Applying online for credit is fast, but security should come first. Use this checklist to protect your
          identity and avoid account compromise when borrowing online.
        </Text>

        <Heading as="h2" size="md">Before You Apply</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Use the lender&apos;s official website only</ListItem>
          <ListItem>Check for HTTPS and valid domain spelling</ListItem>
          <ListItem>Avoid public Wi-Fi; use private network or mobile data</ListItem>
          <ListItem>Prepare only required documents, nothing extra</ListItem>
        </List>

        <Heading as="h2" size="md">During the Application</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Never share online banking password or card PIN</ListItem>
          <ListItem>Do not send ID through unsecured chat apps</ListItem>
          <ListItem>Review every consent checkbox before submitting</ListItem>
          <ListItem>Take screenshots of final quoted terms</ListItem>
        </List>

        <Heading as="h2" size="md">After Approval</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Save contract and repayment schedule immediately</ListItem>
          <ListItem>Enable alerts for transactions and login attempts</ListItem>
          <ListItem>Set payment reminders or autopay</ListItem>
          <ListItem>Monitor account activity weekly</ListItem>
        </List>

        <Heading as="h2" size="md">Security Red Flags</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Requests for upfront payment before funding</ListItem>
          <ListItem>Pressure to decide "within minutes"</ListItem>
          <ListItem>Support contacts that change constantly</ListItem>
          <ListItem>Links sent from non-official domains</ListItem>
        </List>

        <Heading as="h2" size="md">Quick Safety Matrix</Heading>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Area</Th>
              <Th>Safe Practice</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td>Website access</Td>
              <Td>Manual URL entry + HTTPS verification</Td>
            </Tr>
            <Tr>
              <Td>Identity docs</Td>
              <Td>Upload only through secure portal</Td>
            </Tr>
            <Tr>
              <Td>Account security</Td>
              <Td>Strong password + 2FA enabled</Td>
            </Tr>
            <Tr>
              <Td>Payment protection</Td>
              <Td>Alerts + due-date reminders</Td>
            </Tr>
          </Tbody>
        </Table>

        <Heading as="h2" size="md">Final Thoughts</Heading>
        <Text>
          Safe online borrowing is mostly about verification and discipline. If any step feels unclear or rushed,
          pause and confirm details before you proceed.
        </Text>
        <Box mt={10} width="100%" textAlign="center">
          <Heading as="h3" size="md" mb={3} color="teal.700">Get an instant loan now with Swift Credit</Heading>
          <Link as={RouterLink} to="/apply" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <Box as="button" bg="teal.500" color="white" px={8} py={3} borderRadius="md" fontWeight="bold" _hover={{ bg: "teal.600" }}>
              Get a Loan
            </Box>
          </Link>
        </Box>
      </VStack>
    </Box>
  );
}
